import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { ProfileProvider, useProfile } from '@/lib/profile-context';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import DashboardLayout from '@/components/layout/DashboardLayout';
import OverviewPage from '@/pages/OverviewPage';
import MetricsPage from '@/pages/MetricsPage';
import AlertsPage from '@/pages/AlertsPage';
import SignalsPage from '@/pages/SignalsPage';
import HistoryPage from '@/pages/HistoryPage';
import AdminPage from '@/pages/AdminPage';
import SettingsPage from '@/pages/SettingsPage';
import AsinRiskPage from '@/pages/AsinRiskPage';
import TasksPage from '@/pages/TasksPage';
import PoaPage from '@/pages/PoaPage';
import AccountsPage from '@/pages/AccountsPage';
import SupportDataPage from '@/pages/SupportDataPage';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireProfile({ children }) {
  const { hasSelection } = useProfile();
  if (!hasSelection) return <Navigate to="/accounts" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/signup" element={user ? <Navigate to="/" replace /> : <SignupPage />} />
      <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<RequireProfile><OverviewPage /></RequireProfile>} />
        <Route path="metrics" element={<RequireProfile><MetricsPage /></RequireProfile>} />
        <Route path="alerts" element={<RequireProfile><AlertsPage /></RequireProfile>} />
        <Route path="signals" element={<RequireProfile><SignalsPage /></RequireProfile>} />
        <Route path="history" element={<RequireProfile><HistoryPage /></RequireProfile>} />
        <Route path="asin-risks" element={<RequireProfile><AsinRiskPage /></RequireProfile>} />
        <Route path="tasks" element={<RequireProfile><TasksPage /></RequireProfile>} />
        <Route path="poa-templates" element={<RequireProfile><PoaPage /></RequireProfile>} />
        <Route path="support-data" element={<RequireProfile><SupportDataPage /></RequireProfile>} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="admin" element={<RequireProfile><AdminRoute><AdminPage /></AdminRoute></RequireProfile>} />
        <Route path="settings" element={<RequireProfile><SettingsPage /></RequireProfile>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <div className="dark min-h-screen bg-background text-foreground">
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
